import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { LocationTrackingSettingsAPI, FeatureAccessAPI } from '../services/api';
import './Configurations.css';
import TeamOrgModal from '../components/TeamOrgModal';
import AttendanceSettingsModal from '../components/AttendanceSettingsModal';
import CalendarAvailabilityModal from './Tasks/components/Modals/CalendarAvailabilityModal';
import { QRCodeSVG } from 'qrcode.react';
import { FiUsers, FiX } from 'react-icons/fi';
import { FaAndroid, FaApple } from 'react-icons/fa';
import Onboarding from './Onboarding';
import usePendingIntegrationConsents from '../hooks/usePendingIntegrationConsents';

const defaultAttendanceSettingsDraft = () => ({
  workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  workStartTime: '09:00',
  workEndTime: '17:00',
  lunchBreakStart: '13:00',
  lunchBreakEnd: '14:00',
  graceTimeInMinutes: 15,
  requireApproval: false,
  requireLocationForAttendance: true,
  geofencingEnabled: true,
  geofencingRadius: 100,
  geofencingLocation: { address: '', coordinates: { lat: null, lng: null } }
});

function Configurations() {
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mode, setMode] = useState('track_save_during_tasks');
  // Feature Access state
  const [faLoading, setFaLoading] = useState(false);
  const [faSaving, setFaSaving] = useState(false);
  const [faError, setFaError] = useState(null);
  const [faSuccess, setFaSuccess] = useState(null);
  const [faDepartments, setFaDepartments] = useState([]);
  const [faFeatures, setFaFeatures] = useState([]);
  const [faMatrix, setFaMatrix] = useState({}); // key: `${departmentId}:${featureKey}` => boolean
  const [faInitialMatrix, setFaInitialMatrix] = useState({});
  const [activeSection, setActiveSection] = useState('feature_access'); // 'feature_access' | 'location'
  const [orgModalDept, setOrgModalDept] = useState(null);
  const [showQuickSetupModal, setShowQuickSetupModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [calendarAvailability, setCalendarAvailability] = useState(null);
  const [selectedDeptIdForQR, setSelectedDeptIdForQR] = useState('');
  const [downloadRole, setDownloadRole] = useState('field');
  const [downloadPlatform, setDownloadPlatform] = useState('android');
  const [showAttendanceSettings, setShowAttendanceSettings] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [settingsDepartmentId, setSettingsDepartmentId] = useState('company');
  const [settingsDraft, setSettingsDraft] = useState(() => defaultAttendanceSettingsDraft());
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsApiError, setSettingsApiError] = useState('');
  const [settingsIsPersisted, setSettingsIsPersisted] = useState(false);
  const [settingsLastSavedSnapshot, setSettingsLastSavedSnapshot] = useState(() => defaultAttendanceSettingsDraft());
  const [settingsSummariesLoading, setSettingsSummariesLoading] = useState(false);
  const [settingsSummaries, setSettingsSummaries] = useState({});
  const integrationConsents = usePendingIntegrationConsents({ includeAll: true });
  const [dataSharingSubmittingId, setDataSharingSubmittingId] = useState(null);
  const [dataSharingDenyId, setDataSharingDenyId] = useState(null);
  const [dataSharingDenyReason, setDataSharingDenyReason] = useState('');
  const [dataSharingMessage, setDataSharingMessage] = useState('');
  const featureDescriptions = {
    ACCOUNTS: 'Quotes, Proformas, Invoices - create, edit, send, PDFs.',
    ORDERS: 'Order templates and customer orders - create, track, fulfill.',
    POS: 'Point of Sale - quick billing/checkout for walk-in sales.',
    INVENTORY: 'Items and services - catalog, pricing, taxes.',
    EXPENSES: 'Expense tracking, approvals, reimbursements, OCR receipt scanning.',
    ATTENDANCE: 'Employee attendance tracking, check-in/out, calendar view.',
    LOCATION: 'Real-time employee location tracking on map.'
  };
  const attendanceColors = useMemo(() => ({
    present: '#22c55e',
    presentBg: '#dcfce7',
    late: '#f59e0b',
    lateBg: '#fef3c7',
    partial: '#8b5cf6',
    partialBg: '#ede9fe',
    absent: '#ef4444',
    absentBg: '#fee2e2',
    weekend: '#f1f5f9',
    today: '#3b82f6',
    todayBg: '#dbeafe',
    border: '#e2e8f0',
    text: '#1e293b',
    textMuted: '#64748b',
    cardBg: '#ffffff',
    pageBg: '#f8fafc'
  }), []);
  const qrLogoSrc = company?.logo || '/logo.png';

  useEffect(() => {
    const load = async () => {
      if (!company?.id) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await LocationTrackingSettingsAPI.get(company.id);
        if (resp?.is_success && resp?.settings?.locationTrackingMode) {
          setMode(resp.settings.locationTrackingMode);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [company?.id]);

  useEffect(() => {
    const loadDepartments = async () => {
      if (!company?.id) return;
      try {
        const resp = await api.get(`/api/departments/${company.id}`);
        if (resp?.data?.is_success) {
          setDepartments(resp.data.departments || []);
        }
      } catch (e) {
        console.error('Failed to load departments for attendance setup', e);
      }
    };
    loadDepartments();
  }, [company?.id]);

  const normalizeAttendanceSettingsFromApi = (settings) => {
    const d = defaultAttendanceSettingsDraft();
    const timeToInput = (t) => (typeof t === 'string' && t.length >= 5 ? t.slice(0, 5) : '');

    const geofence = settings?.geofencingLocation;
    const coords = geofence?.coordinates;
    const coordsObj = coords && typeof coords === 'object' && !Array.isArray(coords) ? coords : null;
    const lat = coordsObj?.lat ?? geofence?.lat ?? null;
    const lng = coordsObj?.lng ?? geofence?.lng ?? null;
    const address = geofence?.address ?? '';

    return {
      ...d,
      workingDays: Array.isArray(settings?.workingDays) ? settings.workingDays : d.workingDays,
      workStartTime: timeToInput(settings?.workStartTime) || d.workStartTime,
      workEndTime: timeToInput(settings?.workEndTime) || d.workEndTime,
      lunchBreakStart: timeToInput(settings?.lunchBreakStart) || d.lunchBreakStart,
      lunchBreakEnd: timeToInput(settings?.lunchBreakEnd) || d.lunchBreakEnd,
      graceTimeInMinutes: Number.isFinite(Number(settings?.graceTimeInMinutes)) ? Number(settings.graceTimeInMinutes) : d.graceTimeInMinutes,
      requireApproval: !!settings?.requireApproval,
      requireLocationForAttendance: !!settings?.requireLocationForAttendance,
      geofencingEnabled: !!settings?.geofencingEnabled,
      geofencingRadius: Number.isFinite(Number(settings?.geofencingRadius)) ? Number(settings.geofencingRadius) : d.geofencingRadius,
      geofencingLocation: {
        address,
        coordinates: { lat: lat == null ? null : Number(lat), lng: lng == null ? null : Number(lng) }
      }
    };
  };

  const loadAttendanceSettings = async (deptId) => {
    if (!company?.id) return;
    setSettingsLoading(true);
    setSettingsApiError('');
    try {
      const { data } = await api.get(`/api/companies/${company.id}/attendance-settings`, {
        params: { departmentId: deptId === 'company' ? 'company' : deptId, createIfMissing: 0 }
      });
      if (!data?.is_success) {
        setSettingsApiError(data?.message || 'Failed to load settings');
        const d = defaultAttendanceSettingsDraft();
        setSettingsIsPersisted(false);
        setSettingsDraft(d);
        setSettingsLastSavedSnapshot(d);
        return;
      }
      const exists = data?.exists != null ? !!data.exists : !!data.settings;
      if (!exists || !data.settings) {
        const d = defaultAttendanceSettingsDraft();
        setSettingsIsPersisted(false);
        setSettingsDraft(d);
        setSettingsLastSavedSnapshot(d);
        return;
      }
      const normalized = normalizeAttendanceSettingsFromApi(data.settings);
      setSettingsIsPersisted(true);
      setSettingsDraft(normalized);
      setSettingsLastSavedSnapshot(normalized);
    } catch (e) {
      setSettingsApiError(e?.response?.data?.message || 'Failed to load settings');
      const d = defaultAttendanceSettingsDraft();
      setSettingsIsPersisted(false);
      setSettingsDraft(d);
      setSettingsLastSavedSnapshot(d);
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadAttendanceSettingsSummaries = async () => {
    if (!company?.id) return;
    setSettingsSummariesLoading(true);
    try {
      const keys = ['company', ...(departments || []).map(d => String(d.id))];
      const results = await Promise.all(keys.map(async (k) => {
        try {
          const { data } = await api.get(`/api/companies/${company.id}/attendance-settings`, {
            params: { departmentId: k === 'company' ? 'company' : k, createIfMissing: 0 }
          });
          const exists = data?.exists != null ? !!data.exists : !!data?.settings;
          const draft = (exists && data?.settings) ? normalizeAttendanceSettingsFromApi(data.settings) : null;
          return [k, { exists, draft }];
        } catch {
          return [k, { exists: false, draft: null }];
        }
      }));
      const next = {};
      results.forEach(([k, v]) => { next[k] = v; });
      setSettingsSummaries(next);
    } finally {
      setSettingsSummariesLoading(false);
    }
  };

  const saveAttendanceSettings = async (deptId) => {
    if (!company?.id) return;
    setSettingsSaving(true);
    setSettingsApiError('');
    try {
      const payload = {
        ...settingsDraft,
        geofencingLocation: settingsDraft.geofencingLocation
      };
      const { data } = await api.put(`/api/companies/${company.id}/attendance-settings`, payload, {
        params: { departmentId: deptId === 'company' ? 'company' : deptId }
      });
      if (!data?.is_success) {
        setSettingsApiError(data?.message || 'Failed to save settings');
        return;
      }
      const normalized = normalizeAttendanceSettingsFromApi(data.settings);
      setSettingsIsPersisted(true);
      setSettingsDraft(normalized);
      setSettingsLastSavedSnapshot(normalized);
      setSettingsSummaries(prev => ({ ...prev, [deptId]: { exists: true, draft: normalized } }));
    } catch (e) {
      setSettingsApiError(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const getOnboardingQrValue = () => {
    const dept = departments.find(d => String(d.id) === String(selectedDeptIdForQR));
    return JSON.stringify({
      companyId: company?.id,
      departmentId: dept?.id || null,
      departmentName: dept?.name || null
    });
  };

  const getDownloadAppUrl = () => {
    if (downloadRole === 'field') {
      return downloadPlatform === 'android'
        ? 'https://play.google.com/store/apps/details?id=com.bloomnav.employee'
        : 'https://apps.apple.com/in/app/bloomnav-employee/id6749189070';
    }
    return downloadPlatform === 'android'
      ? 'https://play.google.com/store/apps/details?id=com.bloomnav.organisation'
      : 'https://apps.apple.com/app/id6745412517';
  };

  useEffect(() => {
    if (!showAttendanceSettings || !company?.id) return;
    loadAttendanceSettings(settingsDepartmentId);
    loadAttendanceSettingsSummaries();
  }, [showAttendanceSettings, company?.id, settingsDepartmentId, departments.length]);

  const settingsIsDirty = useMemo(() => {
    try {
      return JSON.stringify(settingsDraft) !== JSON.stringify(settingsLastSavedSnapshot);
    } catch {
      return true;
    }
  }, [settingsDraft, settingsLastSavedSnapshot]);

  useEffect(() => {
    const loadFeatureAccess = async () => {
      if (!company?.id) return;
      setFaLoading(true);
      setFaError(null);
      try {
        const resp = await FeatureAccessAPI.getPolicies(company.id);
        if (resp?.is_success) {
          const { departments = [], features = [], policies = [] } = resp;
          setFaDepartments(departments);
          setFaFeatures(features);
          const next = {};
          for (const p of policies) {
            if (p.allow) next[`${p.departmentId}:${p.featureKey}`] = true;
          }
          // Normalize: if ORDERS/POS/ACCOUNTS is enabled, ensure INVENTORY is enabled for that department
          const triggerKeys = new Set(['ORDERS', 'POS', 'ACCOUNTS']);
          const hasInventory = features.some(f => f.key === 'INVENTORY');
          if (hasInventory) {
            for (const d of departments) {
              const anyTrigger = Array.from(triggerKeys).some(tk => !!next[`${d.id}:${tk}`]);
              if (anyTrigger) {
                next[`${d.id}:INVENTORY`] = true;
              }
            }
          }
          setFaMatrix(next);
          setFaInitialMatrix(next);
        }
      } catch (e) {
        console.error(e);
        setFaError('Failed to load feature access');
      } finally {
        setFaLoading(false);
      }
    };
    loadFeatureAccess();
  }, [company?.id]);

  const isDirty = React.useMemo(() => {
    const sortKeys = (obj) => Object.keys(obj).sort();
    const aKeys = sortKeys(faInitialMatrix);
    const bKeys = sortKeys(faMatrix);
    if (aKeys.length !== bKeys.length) return true;
    for (let i = 0; i < aKeys.length; i++) {
      const k = aKeys[i];
      if (k !== bKeys[i]) return true;
      if (!!faInitialMatrix[k] !== !!faMatrix[k]) return true;
    }
    return false;
  }, [faInitialMatrix, faMatrix]);

  const handleSave = async () => {
    if (!company?.id) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await LocationTrackingSettingsAPI.update(company.id, { locationTrackingMode: mode });
      if (resp?.is_success) {
        setSuccess('Settings saved');
      } else {
        setError(resp?.message || 'Failed to save');
      }
    } catch (e) {
      console.error(e);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const toggleCell = (departmentId, featureKey) => {
    const k = `${departmentId}:${featureKey}`;
    setFaMatrix((prev) => {
      const nextValue = !prev[k];
      const next = { ...prev, [k]: nextValue };
      // If enabling a dependent feature (ACCOUNTS/ORDERS/POS), auto-enable INVENTORY
      const triggerKeys = new Set(['ORDERS', 'POS', 'ACCOUNTS']);
      const hasInventory = faFeatures.some(f => f.key === 'INVENTORY');
      if (nextValue && triggerKeys.has(featureKey) && hasInventory) {
        next[`${departmentId}:INVENTORY`] = true;
      }
      return next;
    });
  };

  const selectAllRow = (departmentId) => {
    setFaMatrix((prev) => {
      const next = { ...prev };
      for (const f of faFeatures) {
        next[`${departmentId}:${f.key}`] = true;
      }
      return next;
    });
  };

  const clearRow = (departmentId) => {
    setFaMatrix((prev) => {
      const next = { ...prev };
      for (const f of faFeatures) {
        next[`${departmentId}:${f.key}`] = false;
      }
      return next;
    });
  };

  const handleSaveFeatureAccess = async () => {
    if (!company?.id) return;
    setFaSaving(true);
    setFaError(null);
    setFaSuccess(null);
    try {
      const policies = [];
      for (const dept of faDepartments) {
        for (const feat of faFeatures) {
          const k = `${dept.id}:${feat.key}`;
          const allow = !!faMatrix[k];
          if (allow) {
            policies.push({ departmentId: dept.id, featureKey: feat.key, allow: true });
          }
        }
      }
      const resp = await FeatureAccessAPI.putPolicies(company.id, policies);
      if (resp?.is_success) {
        setFaSuccess('Feature access saved');
        setFaInitialMatrix(faMatrix);
      } else {
        setFaError(resp?.message || 'Failed to save feature access');
      }
    } catch (e) {
      console.error(e);
      setFaError('Failed to save feature access');
    } finally {
      setFaSaving(false);
      setTimeout(() => setFaSuccess(null), 3000);
    }
  };

  const handleResetFeatureAccess = () => {
    setFaMatrix(faInitialMatrix);
    setFaError(null);
    setFaSuccess(null);
  };

  const formatConsentDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const getDataSharingStatus = (consent) => {
    if (consent.status === 'approved' && consent.isActive) return { label: 'Approved', bg: '#ecfdf5', border: '#bbf7d0', color: '#047857' };
    if (consent.status === 'approved' && !consent.isActive) return { label: 'Revoked', bg: '#f8fafc', border: '#e2e8f0', color: '#475569' };
    if (consent.status === 'denied') return { label: 'Denied', bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' };
    return { label: 'Pending consent', bg: '#fffbeb', border: '#fde68a', color: '#92400e' };
  };

  const handleApproveDataSharing = async (consent) => {
    setDataSharingSubmittingId(consent.id);
    setDataSharingMessage('');
    try {
      await integrationConsents.approve(consent.id);
      setDataSharingMessage(`Data sharing approved for ${consent.merchantName || 'merchant'}.`);
    } catch (e) {
      setDataSharingMessage(e?.response?.data?.message || 'Failed to approve data sharing request.');
    } finally {
      setDataSharingSubmittingId(null);
    }
  };

  const handleDenyDataSharing = async (consent) => {
    setDataSharingSubmittingId(consent.id);
    setDataSharingMessage('');
    try {
      await integrationConsents.deny(consent.id, dataSharingDenyReason);
      setDataSharingMessage(`Data sharing denied for ${consent.merchantName || 'merchant'}.`);
      setDataSharingDenyId(null);
      setDataSharingDenyReason('');
    } catch (e) {
      setDataSharingMessage(e?.response?.data?.message || 'Failed to deny data sharing request.');
    } finally {
      setDataSharingSubmittingId(null);
    }
  };

  return (
    <div className="page-container configurations-page">
    
      <div className="page-content">
      <div className="config-page-header">
        <div>
          <h2 style={{ fontSize: 16, margin: 0 }}>Configurations</h2>
          <p className="page-subtitle" style={{ margin: '4px 0 0', fontSize: 12 }}>
            Manage application configurations and preferences.
          </p>
        </div>
        <button
          type="button"
          className="config-quick-setup-btn"
          onClick={() => setShowQuickSetupModal(true)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82L4.21 7.1a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .67.39 1.28 1 1.51.17.07.34.1.51.1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          Quick setup
        </button>
      </div>
      <div className="config-cards" style={{ display:'flex', gap:8, flexWrap:'nowrap', marginBottom: 10, overflowX: 'auto' }}>
        <button
          type="button"
          onClick={() => setActiveSection('feature_access')}
          className={`config-card ${activeSection === 'feature_access' ? 'active' : ''}`}
          style={{
            padding:'7px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:activeSection === 'feature_access' ? '#000' : '#fff', cursor:'pointer',
            minWidth:145
          }}
        >
          <div className="config-card-title" style={{ fontSize: 13, fontWeight: 600, marginBottom: 0,color:activeSection === 'feature_access' ? '#fff' : '#000' }}>Feature Access</div>
          <div className="config-card-subtitle" style={{ fontSize: 11, color:activeSection === 'feature_access' ? '#fff' : '#000' }}>Roles & Permissions</div>
        </button>
        <button
          type="button"
          onClick={() => setActiveSection('location')}
          className={`config-card ${activeSection === 'location' ? 'active' : ''}`}
          style={{
            padding:'7px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:activeSection === 'location' ? '#000' : '#fff', cursor:'pointer',
            minWidth:145
          }}
        >
          <div className="config-card-title" style={{ fontSize: 13, fontWeight: 600, marginBottom: 0,color:activeSection === 'location' ? '#fff' : '#000' }}>Location Tracking</div>
          <div className="config-card-subtitle" style={{ fontSize: 11, color:activeSection === 'location' ? '#fff' : '#000' }}>Field staff GPS</div>
        </button>
        <button
          type="button"
          onClick={() => setActiveSection('data_sharing')}
          className={`config-card ${activeSection === 'data_sharing' ? 'active' : ''}`}
          style={{
            padding:'7px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:activeSection === 'data_sharing' ? '#000' : '#fff', cursor:'pointer',
            minWidth:155
          }}
        >
          <div className="config-card-title" style={{ fontSize: 13, fontWeight: 600, marginBottom: 0,color:activeSection === 'data_sharing' ? '#fff' : '#000' }}>Data Sharing</div>
          <div className="config-card-subtitle" style={{ fontSize: 11, color:activeSection === 'data_sharing' ? '#fff' : '#000' }}>API access consent</div>
        </button>
        <button
          type="button"
          onClick={() => setShowCalendarModal(true)}
          className="config-card"
          style={{
            padding:'7px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer',
            minWidth:170
          }}
        >
          <div className="config-card-title" style={{ fontSize: 13, fontWeight: 600, marginBottom: 0, color:'#000' }}>Calendar Availability</div>
          <div className="config-card-subtitle" style={{ fontSize: 11, color:'#000' }}>Personal booking</div>
        </button>
        <button
          type="button"
          onClick={() => setShowAttendanceSettings(true)}
          className="config-card"
          style={{
            padding:'7px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer',
            minWidth:170
          }}
        >
          <div className="config-card-title" style={{ fontSize: 13, fontWeight: 600, marginBottom: 0, color:'#000' }}>Attendance Setup</div>
          <div className="config-card-subtitle" style={{ fontSize: 11, color:'#000' }}>Schedule & geofencing</div>
        </button>
        <button
          type="button"
          onClick={() => setShowInviteModal(true)}
          className="config-card"
          style={{
            padding:'7px 10px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer',
            minWidth:150
          }}
        >
          <div className="config-card-title" style={{ fontSize: 13, fontWeight: 600, marginBottom: 0, color:'#000' }}>Invite User</div>
          <div className="config-card-subtitle" style={{ fontSize: 11, color:'#000' }}>Onboarding QR</div>
        </button>
      </div>
        {activeSection === 'feature_access' && (
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>Feature Access by Team</h2>
            <p className="page-subtitle" style={{ margin: 0 }}>
              Grant teams access to features. Inventory is required and auto-enabled when Accounts, Orders, or POS are enabled.
            </p>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                  Tip: Enabling Accounts, Orders, or POS auto-enables Inventory for that team.
                </div>
          <div className="card-body">
            {faLoading ? (
              <p>Loading…</p>
            ) : (
              <>
                {faError && <div className="error-message" style={{ marginBottom: 8 }}>{faError}</div>}
                {faSuccess && <div className="success-message" style={{ marginBottom: 8 }}>{faSuccess}</div>}
                <div style={{ overflowX: 'auto' }}>
                  <table className="feature-access-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Team</th>
                        {faFeatures.map((f) => (
                          <th
                            key={f.key}
                            style={{ textAlign: 'center', padding: 8, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}
                            title={featureDescriptions[f.key] || f.label}
                          >
                            {f.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {faDepartments.map((d) => (
                        <tr key={d.id}>
                          <td style={{ padding: 8, borderBottom: '1px solid #f5f5f5', minWidth: 180 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'space-between' }}>
                              <button
                                type="button"
                                className="link-button"
                                onClick={() => setOrgModalDept(d)}
                                title="View team org structure"
                                style={{ background:'transparent', border:'none', padding:0, cursor:'pointer', fontWeight:600 }}
                              >
                                {d.name}
                              </button>
                              <span style={{ display:'flex', gap:6 }}>
                                <button
                                  type="button"
                                  className="link-button"
                                  onClick={() => selectAllRow(d.id)}
                                  style={{ fontSize: 12, color:'#2563eb', background:'transparent', border:'none', cursor:'pointer', padding:0 }}
                                >
                                  Select all
                                </button>
                                <span style={{ color:'#d1d5db' }}>|</span>
                                <button
                                  type="button"
                                  className="link-button"
                                  onClick={() => clearRow(d.id)}
                                  style={{ fontSize: 12, color:'#6b7280', background:'transparent', border:'none', cursor:'pointer', padding:0 }}
                                >
                                  Clear
                                </button>
                              </span>
                            </div>
                          </td>
                          {faFeatures.map((f) => {
                            const k = `${d.id}:${f.key}`;
                            const checked = !!faMatrix[k];
                            const isInventory = f.key === 'INVENTORY';
                            const triggerKeys = new Set(['ORDERS', 'POS', 'ACCOUNTS']);
                            const isForcedInventory = isInventory && Array.from(triggerKeys).some(tk => !!faMatrix[`${d.id}:${tk}`]);
                            const effectiveChecked = isForcedInventory ? true : checked;
                            return (
                              <td key={k} style={{ textAlign: 'center', padding: 8, borderBottom: '1px solid #f5f5f5' }}>
                                <input
                                  type="checkbox"
                                  checked={effectiveChecked}
                                  disabled={isForcedInventory}
                                  onChange={() => !isForcedInventory && toggleCell(d.id, f.key)}
                                  title={featureDescriptions[f.key] || f.label}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-primary" onClick={handleSaveFeatureAccess} disabled={faSaving || !isDirty}>
                    {faSaving ? 'Saving…' : (isDirty ? 'Save changes' : 'Saved')}
                  </button>
                  <button className="btn btn-secondary" onClick={handleResetFeatureAccess} disabled={!isDirty}>
                    Reset
                  </button>
                </div>
                <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', border: '1px solid #eef2f7', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12 }}>What each feature includes</div>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 12 }}>
                    <li><strong>Accounts</strong>: Create, view, update, reject Quotes, Proformas, Invoices; send to customers; view/print PDFs.</li>
                    <li><strong>Orders</strong>: Maintain order templates, accept customer orders, update statuses, mark as paid, add tasks.</li>
                    <li><strong>POS</strong>: Point-of-sale checkout for quick walk-in billing and receipts.</li>
                    <li><strong>Inventory</strong>: Manage item/service catalog, pricing, taxes - required by other features.</li>
                    <li><strong>Expenses</strong>: Capture and process expenses with approvals, reimbursements, and payment tracking.</li>
                    <li><strong>Attendance</strong>: Track employee attendance with check-in/out calendar views.</li>
                    <li><strong>Location</strong>: View and manage employee location tracking on the map.</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
        )}
        {activeSection === 'location' && (
        <div className="card">
          <div className="card-header">
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>Field staff location tracking</h2>
          </div>
          <div className="card-body">
            {loading ? (
              <p>Loading…</p>
            ) : (
              <>
                {error && <div className="error-message" style={{ marginBottom: 8 }}>{error}</div>}
                {success && <div className="success-message" style={{ marginBottom: 8 }}>{success}</div>}

                <div role="radiogroup" aria-label="Location tracking mode" style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {/* <label style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                    <input
                      type="radio"
                      name="locationTrackingMode"
                      value="track_save_during_tasks"
                      checked={mode === 'track_save_during_tasks'}
                      onChange={(e) => setMode(e.target.value)}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, lineHeight: 1.2 }}>Track and save only during active tasks</div>
                      <div style={{ fontSize: 11, lineHeight: 1.2, color: '#6b7280' }}>Collect and store location only when an active task is running.</div>
                    </div>
                  </label> */}
                  <label style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                    <input
                      type="radio"
                      name="locationTrackingMode"
                      value="track_save_when_logged_in"
                      checked={mode === 'track_save_when_logged_in'}
                      onChange={(e) => setMode(e.target.value)}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, lineHeight: 1.2 }}>Track and save when logged in to work</div>
                      <div style={{ fontSize: 11, lineHeight: 1.2, color: '#6b7280' }}>Collect and store location anytime the employee is logged in.</div>
                    </div>
                  </label>
                  <label style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                    <input
                      type="radio"
                      name="locationTrackingMode"
                      value="do_not_track"
                      checked={mode === 'do_not_track'}
                      onChange={(e) => setMode(e.target.value)}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, lineHeight: 1.2 }}>Do not track</div>
                      <div style={{ fontSize: 11, lineHeight: 1.2, color: '#6b7280' }}>Location data won’t be shared with the company.</div>
                    </div>
                  </label>
                </div>

                <div style={{ marginTop: 10, display:'flex', gap:8 }}>
                  <button className="primary-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </>
            )}
          </div>
        </div>
        )}
        {activeSection === 'data_sharing' && (
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>Data Sharing & API Access</h2>
            <p className="page-subtitle" style={{ margin: 0 }}>
              Review merchant requests to access your company data through the BloomNav integration API.
            </p>
          </div>
          <div className="card-body">
            <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: 13, marginBottom: 12 }}>
              Approving a request enables read access for attendance, employee, and attendance settings data. Access stays blocked until approval.
            </div>
            {integrationConsents.loading ? (
              <p>Loading data sharing requests…</p>
            ) : (
              <>
                {integrationConsents.error && <div className="error-message" style={{ marginBottom: 8 }}>{integrationConsents.error}</div>}
                {dataSharingMessage && <div className="success-message" style={{ marginBottom: 8 }}>{dataSharingMessage}</div>}
                {integrationConsents.consents.length === 0 ? (
                  <div style={{ padding: 18, background: '#f9fafb', border: '1px solid #eef2f7', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>No data sharing access records</div>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>Merchant API access requests and approved data sharing records will appear here.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {integrationConsents.consents.map((consent) => {
                      const status = getDataSharingStatus(consent);
                      const isPending = consent.status === 'pending' && !consent.isActive;
                      return (
                        <div key={consent.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{consent.merchantName || 'Merchant'}</div>
                              <div style={{ color: '#6b7280', fontSize: 12, marginTop: 3 }}>{consent.merchantContactEmail || 'No contact email'}</div>
                              <div style={{ color: '#6b7280', fontSize: 12, marginTop: 3 }}>Requested {formatConsentDate(consent.requestedAt)}</div>
                              {consent.respondedAt && (
                                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 3 }}>Responded {formatConsentDate(consent.respondedAt)}</div>
                              )}
                              {consent.denialReason && (
                                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 3 }}>Reason: {consent.denialReason}</div>
                              )}
                            </div>
                            <span style={{ background: status.bg, border: `1px solid ${status.border}`, color: status.color, borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 700 }}>
                              {status.label}
                            </span>
                          </div>
                          {isPending && dataSharingDenyId === consent.id && (
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12, fontSize: 12, fontWeight: 600 }}>
                              Denial reason (optional)
                              <textarea
                                value={dataSharingDenyReason}
                                onChange={(e) => setDataSharingDenyReason(e.target.value)}
                                placeholder="Add a note for the merchant"
                                style={{ minHeight: 76, border: '1px solid #d1d5db', borderRadius: 8, padding: 10, font: 'inherit' }}
                              />
                            </label>
                          )}
                          {isPending && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                              {dataSharingDenyId === consent.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    disabled={dataSharingSubmittingId === consent.id}
                                    onClick={() => { setDataSharingDenyId(null); setDataSharingDenyReason(''); }}
                                  >
                                    Back
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    disabled={dataSharingSubmittingId === consent.id}
                                    onClick={() => handleDenyDataSharing(consent)}
                                  >
                                    {dataSharingSubmittingId === consent.id ? 'Denying…' : 'Confirm deny'}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    disabled={dataSharingSubmittingId === consent.id}
                                    onClick={() => setDataSharingDenyId(consent.id)}
                                  >
                                    Deny
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={dataSharingSubmittingId === consent.id}
                                    onClick={() => handleApproveDataSharing(consent)}
                                  >
                                    {dataSharingSubmittingId === consent.id ? 'Approving…' : 'Approve & enable access'}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </div>
      {orgModalDept && (
        <TeamOrgModal department={orgModalDept} onClose={() => setOrgModalDept(null)} />
      )}
      <CalendarAvailabilityModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        availability={calendarAvailability || undefined}
        onSave={(next) => setCalendarAvailability(next)}
      />
      <AttendanceSettingsModal
        isOpen={showAttendanceSettings}
        onClose={() => setShowAttendanceSettings(false)}
        colors={attendanceColors}
        departments={departments}
        selectedDepartmentId={settingsDepartmentId}
        onChangeDepartment={(nextDeptId) => setSettingsDepartmentId(nextDeptId || 'company')}
        settingsDraft={settingsDraft}
        setSettingsDraft={setSettingsDraft}
        isLoading={settingsLoading}
        isSaving={settingsSaving}
        error={settingsApiError}
        onSave={() => saveAttendanceSettings(settingsDepartmentId)}
        onReset={() => loadAttendanceSettings(settingsDepartmentId)}
        isDirty={settingsIsDirty}
        isPersisted={settingsIsPersisted}
        summariesLoading={settingsSummariesLoading}
        summaries={settingsSummaries}
      />
      {showInviteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 20
          }}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 640,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${attendanceColors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: attendanceColors.text }}>Invite User</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: attendanceColors.textMuted }}>Share QR codes with new team members</p>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="btn btn--outline btn--icon-only"
                style={{ width: 32, minWidth: 32, height: 32, minHeight: 32, borderRadius: 999 }}
                aria-label="Close"
                title="Close"
              >
                <FiX size={16} />
              </button>
            </div>

            {departments.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: attendanceColors.textMuted }}>
                <FiUsers size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>No Teams Found</div>
                <div style={{ fontSize: 13 }}>Create a team first to generate onboarding QR codes.</div>
              </div>
            ) : (
              <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: attendanceColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                    Download App
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
                    <button
                      onClick={() => setDownloadRole('field')}
                      className={`btn btn--outline btn--sm ${downloadRole === 'field' ? 'active' : ''}`}
                      style={{ borderRadius: 20 }}
                    >
                      Field Staff
                    </button>
                    <button
                      onClick={() => setDownloadRole('supervisor')}
                      className={`btn btn--outline btn--sm ${downloadRole === 'supervisor' ? 'active' : ''}`}
                      style={{ borderRadius: 20 }}
                    >
                      Supervisor
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
                    <button
                      onClick={() => setDownloadPlatform('android')}
                      className={`btn btn--outline btn--icon-only ${downloadPlatform === 'android' ? 'active' : ''}`}
                      style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, borderRadius: 999 }}
                      aria-label="Android"
                      title="Android"
                    >
                      <FaAndroid size={18} />
                    </button>
                    <button
                      onClick={() => setDownloadPlatform('ios')}
                      className={`btn btn--outline btn--icon-only ${downloadPlatform === 'ios' ? 'active' : ''}`}
                      style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, borderRadius: 999 }}
                      aria-label="iOS"
                      title="iOS"
                    >
                      <FaApple size={18} />
                    </button>
                  </div>
                  <div style={{
                    padding: 16,
                    background: '#fff',
                    borderRadius: 12,
                    border: `1px solid ${attendanceColors.border}`,
                    display: 'inline-block'
                  }}>
                    <QRCodeSVG
                      value={getDownloadAppUrl()}
                      size={140}
                      imageSettings={{ src: qrLogoSrc, height: 28, width: 28, excavate: true }}
                      fgColor="#1e293b"
                      bgColor="#ffffff"
                    />
                  </div>
                  <div style={{ fontSize: 12, color: attendanceColors.text, marginTop: 10, fontWeight: 500 }}>
                    {downloadRole === 'field' ? 'Field Staff' : 'Supervisor'} App ({downloadPlatform === 'android' ? 'Android' : 'iOS'})
                  </div>
                  <div style={{ fontSize: 11, color: attendanceColors.textMuted, marginTop: 4 }}>
                    Scan to install from store
                  </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: attendanceColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                    Select Team
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
                    {departments.map(d => (
                      <button
                        key={d.id}
                        onClick={() => setSelectedDeptIdForQR(String(d.id))}
                        className={`btn btn--outline btn--sm ${String(d.id) === String(selectedDeptIdForQR) ? 'active' : ''}`}
                        style={{ borderRadius: 20 }}
                      >
                        {d.name}
                      </button>
                    ))}
                  </div>
                  <div style={{
                    padding: 20,
                    background: '#fff',
                    borderRadius: 12,
                    border: `1px solid ${attendanceColors.border}`,
                    display: 'inline-block'
                  }}>
                    <QRCodeSVG
                      value={getOnboardingQrValue()}
                      size={180}
                      imageSettings={{ src: qrLogoSrc, height: 32, width: 32, excavate: true }}
                      level="M"
                      fgColor="#1e293b"
                      bgColor="#ffffff"
                    />
                  </div>
                  <div style={{ fontSize: 13, color: attendanceColors.text, marginTop: 12, fontWeight: 500 }}>
                    Scan to join {departments.find(d => String(d.id) === String(selectedDeptIdForQR))?.name || 'team'}
                  </div>
                  <div style={{ fontSize: 11, color: attendanceColors.textMuted, marginTop: 4 }}>
                    Keep screen brightness high for faster scanning
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showQuickSetupModal && (
        <div
          className="config-quick-setup-modal-backdrop"
          onClick={() => setShowQuickSetupModal(false)}
          role="presentation"
        >
          <div
            className="config-quick-setup-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Quick setup onboarding"
          >
            <Onboarding
              embedded
              onClose={() => setShowQuickSetupModal(false)}
              onCompleted={() => setShowQuickSetupModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Configurations;


